/**
 * WebAuthn Browser API Wrapper
 *
 * Handles the browser-side WebAuthn operations:
 * - Registration (navigator.credentials.create)
 * - Authentication (navigator.credentials.get)
 *
 * Security properties:
 * - Origin-bound: credentials are tied to the origin that created them
 * - Challenge-response: prevents replay attacks
 * - Public-key crypto: no shared secrets
 * - User verification: requires biometric/PIN on the authenticator
 * - Cross-device: supports QR code flow for phone-as-authenticator
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AssertionOptions {
  rpId: string;
  challenge: string; // base64url
  allowCredentials: Array<{
    type: "public-key";
    id: string; // base64url
    transports?: string[];
  }>;
  userVerification: "required" | "preferred" | "discouraged";
  timeout: number;
}

export interface CreationOptions {
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string; // base64url
  pubKeyCredParams: Array<{ type: "public-key"; alg: number }>;
  authenticatorSelection?: {
    authenticatorAttachment?: "platform" | "cross-platform";
    residentKey?: "required" | "preferred" | "discouraged";
    userVerification?: "required" | "preferred" | "discouraged";
  };
  excludeCredentials?: Array<{
    type: "public-key";
    id: string;
    transports?: string[];
  }>;
  timeout?: number;
  attestation?: "none" | "direct" | "indirect";
}

export interface AuthenticationResult {
  id: string; // base64url credential ID
  rawId: string; // base64url
  type: "public-key";
  response: {
    authenticatorData: string; // base64url
    clientDataJSON: string; // base64url
    signature: string; // base64url
    userHandle?: string; // base64url
  };
}

export interface RegistrationResult {
  id: string;
  rawId: string;
  type: "public-key";
  response: {
    attestationObject: string; // base64url
    clientDataJSON: string; // base64url
    transports?: string[];
  };
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ─── Feature Detection ────────────────────────────────────────────────────────

/**
 * Check if WebAuthn is supported in this browser.
 */
export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator.credentials !== "undefined"
  );
}

/**
 * Check if the browser supports conditional UI (autofill-assisted passkeys).
 */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  const pkc = PublicKeyCredential as any;
  if (typeof pkc.isConditionalMediationAvailable === "function") {
    return pkc.isConditionalMediationAvailable();
  }
  return false;
}

// ─── Authentication (Sign In) ─────────────────────────────────────────────────

/**
 * Start WebAuthn authentication (sign in with passkey).
 *
 * This calls navigator.credentials.get() which:
 * - Shows a QR code for cross-device authentication (phone)
 * - Or prompts for local biometric (Touch ID, Windows Hello)
 *
 * @param options - Assertion options from the server (CreateAuthChallenge Lambda)
 * @returns The assertion response to send back to the server
 */
export async function startAuthentication(
  options: AssertionOptions
): Promise<AuthenticationResult> {
  if (!isWebAuthnSupported()) {
    throw new Error("WebAuthn is not supported in this browser");
  }

  const publicKeyOptions: PublicKeyCredentialRequestOptions = {
    rpId: options.rpId,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: options.allowCredentials.map((cred) => ({
      type: cred.type,
      id: base64urlToBuffer(cred.id),
      transports: cred.transports as AuthenticatorTransport[] | undefined,
    })),
    userVerification: options.userVerification,
    timeout: options.timeout,
  };

  const credential = (await navigator.credentials.get({
    publicKey: publicKeyOptions,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error("Authentication was cancelled or timed out");
  }

  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: "public-key",
    response: {
      authenticatorData: bufferToBase64url(response.authenticatorData),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle
        ? bufferToBase64url(response.userHandle)
        : undefined,
    },
  };
}

// ─── Registration (Enroll Passkey) ────────────────────────────────────────────

/**
 * Start WebAuthn registration (create a new passkey).
 *
 * This calls navigator.credentials.create() which:
 * - Prompts the user to create a passkey on their device/phone
 * - For cross-device: shows QR code to scan with phone
 *
 * @param options - Creation options from the server (register-begin API)
 * @returns The attestation response to send back to the server
 */
export async function startRegistration(
  options: CreationOptions
): Promise<RegistrationResult> {
  if (!isWebAuthnSupported()) {
    throw new Error("WebAuthn is not supported in this browser");
  }

  const publicKeyOptions: PublicKeyCredentialCreationOptions = {
    rp: options.rp,
    user: {
      id: base64urlToBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    challenge: base64urlToBuffer(options.challenge),
    pubKeyCredParams: options.pubKeyCredParams,
    authenticatorSelection: options.authenticatorSelection,
    excludeCredentials: options.excludeCredentials?.map((cred) => ({
      type: cred.type,
      id: base64urlToBuffer(cred.id),
      transports: cred.transports as AuthenticatorTransport[] | undefined,
    })),
    timeout: options.timeout,
    attestation: options.attestation || "none",
  };

  const credential = (await navigator.credentials.create({
    publicKey: publicKeyOptions,
  })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error("Registration was cancelled or timed out");
  }

  const response = credential.response as AuthenticatorAttestationResponse;

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: "public-key",
    response: {
      attestationObject: bufferToBase64url(response.attestationObject),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      transports: response.getTransports?.() as string[] | undefined,
    },
  };
}
